import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agreementPartiesApi, type AgreementPartiesListParams } from '../api/agreementParties';
import { ApiError } from '../api/client';
import type {
  AgreementParty, AgreementPartyCreateInput, AgreementPartyUpdateInput, Page, UUID, WriteResult,
} from '../api/types';

export const apKeys = {
  all: ['agreement-parties'] as const,
  list:   (p: AgreementPartiesListParams) => ['agreement-parties', 'list', p] as const,
  detail: (id: UUID) => ['agreement-parties', 'detail', id] as const,
};

export function useAgreementParties(params: AgreementPartiesListParams) {
  return useQuery<Page<AgreementParty>, ApiError>({
    queryKey: apKeys.list(params),
    queryFn: () => agreementPartiesApi.list(params),
  });
}
export function useAgreementParty(id: UUID | undefined) {
  return useQuery<AgreementParty, ApiError>({
    queryKey: apKeys.detail(id ?? ''), queryFn: () => agreementPartiesApi.get(id!), enabled: !!id,
  });
}
export function useCreateAgreementParty() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementParty>, ApiError, AgreementPartyCreateInput>({
    mutationFn: agreementPartiesApi.create,
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: apKeys.all }); if (!r.pending) qc.setQueryData(apKeys.detail(r.entity.id), r.entity); },
  });
}
export function useUpdateAgreementParty(id: UUID) {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementParty>, ApiError, AgreementPartyUpdateInput>({
    mutationFn: (p) => agreementPartiesApi.update(id, p),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: apKeys.all }); if (!r.pending) qc.setQueryData(apKeys.detail(id), r.entity); },
  });
}
export function useDeleteAgreementParty() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementParty>, ApiError, UUID>({
    mutationFn: agreementPartiesApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: apKeys.all }),
  });
}
export function useRestoreAgreementParty() {
  const qc = useQueryClient();
  return useMutation<WriteResult<AgreementParty>, ApiError, UUID>({
    mutationFn: agreementPartiesApi.restore,
    onSuccess: () => qc.invalidateQueries({ queryKey: apKeys.all }),
  });
}
